// One shared mutations hook for every email surface (/inbox accordion, pop-out,
// AccountDetail tabs). Optimistic patches flow through lib/email/cache.ts so all
// surfaces update instantly and consistently; server truth is re-synced by
// invalidation + the 60s inbox poll (the server busts its own cache on every write).

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { api, emailApi, fetchWithAuth, forwardInvoiceToMakersHub } from "@/lib/api";
import {
  accountIdForAction,
  invalidateEmailSurfaces,
  patchAccountEmail,
  patchInboxMessage,
  type EmailPatch,
} from "@/lib/email/cache";
import { formatEmailAsNote } from "@/lib/email/emailNoteFormat";
import { isPersonalRow } from "@/lib/email/personalMail";
import type { UnifiedEmail } from "@/lib/email/types";

// "My Mail" (Phase 2): claim/assign/pin/waitingOn/forwardToMakersHub/saveAsNote don't apply to a
// personal mailbox row — the server rejects the write anyway (rejectIfPersonalMailboxTarget in
// routes.ts), but this catches it before a request is even made and no-ops with a dev warning
// instead of a failed-request toast, matching server/lib/emailAccounts.ts's isPersonalMailboxRow rule.
function warnPersonalRowNoOp(action: string) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[useEmailActions] "${action}" is not available on a personal mailbox row — no-op`);
  }
}

export type EmailActionScope = { surface: "inbox" } | { surface: "account"; accountId: string };

export interface UpsertTaskInput {
  status: string;
  assignedToUserId: string | null;
  /** Display name for the optimistic patch (the server responds with the join, but we patch first). */
  assignedToName?: string | null;
  notes?: string | null;
}

/** The four actions a multi-select can apply at once. Each mirrors exactly one single-row
 *  action above, so a bulk write can never diverge from what the same click does on one row. */
export type BulkEmailAction =
  | { type: "read"; isRead: boolean }
  | { type: "assign"; user: { id: string; name: string } | null } // null = unassign
  | { type: "flag"; flagged: boolean }
  | { type: "categorize"; label: string };

/** Hard ceiling on one bulk request. The server fans out per message (Graph + archive), so an
 *  unbounded selection is a request that times out halfway and leaves the user guessing. */
const BULK_MAX = 200;

/** ENG-96: `onTouch` fires for every row this surface optimistically patches, so the
 *  caller can keep that row listed under a filter pill until the pill changes. Additive
 *  second parameter rather than extending EmailActionScope (a discriminated union). */
export function useEmailActions(
  scope: EmailActionScope,
  opts?: { onTouch?: (emailId: string) => void },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  /** True while a bulk action is in flight — the bulk bar disables itself off this. */
  const [bulkBusy, setBulkBusy] = useState(false);

  const setBusyFor = (id: string, value: boolean) =>
    setBusy((prev) => ({ ...prev, [id]: value }));

  /** Same as setBusyFor, in one state update — a bulk action touches up to BULK_MAX rows. */
  const setBusyForAll = (ids: string[], value: boolean) =>
    setBusy((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = value;
      return next;
    });

  const patch = (email: UnifiedEmail, p: EmailPatch) => {
    opts?.onTouch?.(email.id); // ENG-96 — read, claim, flag, assign, categories
    patchInboxMessage(queryClient, email.mailbox, email.id, p);
    // v1.5.71 seam fix: patch the row's CLIENT cache on every surface, not only when this
    // hook is account-scoped — an /inbox or pop-out action used to leave the account tab
    // stale for its 2-min staleTime. No-op when that client's cache isn't loaded.
    const acct = accountIdForAction(scope, email);
    if (acct) patchAccountEmail(queryClient, acct, email.id, p);
  };

  /** invalidateEmailSurfaces options for this row's client (see accountIdForAction). */
  const acctOpt = (email: UnifiedEmail): { accountId?: string } => {
    const acct = accountIdForAction(scope, email);
    return acct ? { accountId: acct } : {};
  };

  /** Claim / assign / status / notes — the single email_tasks write path. */
  const upsertTask = async (email: UnifiedEmail, input: UpsertTaskInput): Promise<any | null> => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("upsertTask"); return null; }
    setBusyFor(email.id, true);
    patch(email, {
      taskStatus: input.status,
      assignedToUserId: input.assignedToUserId,
      ...(input.assignedToName !== undefined ? { assignedToName: input.assignedToName } : {}),
      ...(input.notes !== undefined ? { taskNotes: input.notes } : {}),
      // Assigning marks read server-side (claim→read, Session 195) — mirror that here.
      ...(input.assignedToUserId ? { isRead: true } : {}),
    });
    try {
      const result = await emailApi.upsertEmailTask({
        graph_message_id: email.id,
        mailbox_address: email.mailbox,
        account_id: email.accountId,
        status: input.status,
        assigned_to_user_id: input.assignedToUserId,
        ...(input.notes !== undefined ? { notes: input.notes } : {}),
      });
      // Keep the account page's live task cache truthful (it wins over embedded overlays).
      queryClient.setQueryData(["email-tasks", email.accountId], (old: any[] | undefined) => {
        const without = (old ?? []).filter((t: any) => t.graph_message_id !== email.id);
        return [result, ...without];
      });
      invalidateEmailSurfaces(queryClient, acctOpt(email));
      return result;
    } catch (err: any) {
      toast({ title: "Could not update email", description: err.message, variant: "destructive" });
      invalidateEmailSurfaces(queryClient, acctOpt(email)); // roll back the optimistic patch
      return null;
    } finally {
      setBusyFor(email.id, false);
    }
  };

  const claim = async (email: UnifiedEmail, user: { id: string; name: string } | null) => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("claim"); return null; }
    const result = await upsertTask(email, {
      status: user ? "assigned" : "new",
      assignedToUserId: user?.id ?? null,
      assignedToName: user?.name ?? null,
    });
    if (result) toast({ title: user ? "Email claimed" : "Unassigned" });
    return result;
  };

  const setStatus = async (email: UnifiedEmail, status: string) => {
    const result = await upsertTask(email, {
      status,
      assignedToUserId: email.assignedToUserId ?? null,
      assignedToName: email.assignedToName ?? null,
    });
    if (result) toast({ title: `Marked ${status.replace("_", " ")}` });
    return result;
  };

  /** Star = durable Waiting-On follow-up (account_id makes it survive the recent window). */
  const toggleFlag = async (email: UnifiedEmail, flagged: boolean) => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("toggleFlag"); return false; }
    patch(email, { flagStatus: flagged ? "flagged" : "notFlagged" });
    try {
      await emailApi.setEmailFlag({
        mailbox: email.mailbox,
        message_id: email.id,
        flagged,
        account_id: email.accountId,
      });
      invalidateEmailSurfaces(queryClient, { ...acctOpt(email), waitingOn: true });
      return true;
    } catch (err: any) {
      patch(email, { flagStatus: email.flagStatus ?? "notFlagged" }); // revert
      toast({ title: "Could not update flag", description: err.message, variant: "destructive" });
      return false;
    }
  };

  /** Manual read/unread toggle, mirrored to Outlook (W4). */
  const setRead = async (email: UnifiedEmail, isRead: boolean) => {
    patch(email, { isRead });
    try {
      await emailApi.markRead(email.mailbox, email.id, isRead);
      return true;
    } catch (err: any) {
      patch(email, { isRead: email.isRead }); // revert
      toast({ title: "Could not update read state", description: err.message, variant: "destructive" });
      return false;
    }
  };

  /** Opening a message: patch caches AND sync the server. The body fetch also marks
   * read server-side, but it's cached for 5 min — re-opening a message that was
   * marked unread would otherwise never reach the server (review finding). The
   * explicit mark-read is fire-and-forget and idempotent server-side. */
  const markReadLocal = (email: UnifiedEmail) => {
    // ENG-96 — deliberately ABOVE the early return, so that merely OPENING a row registers
    // it, not just mutating one. This is defence in depth, not the load-bearing path: every
    // action that can actually evict a row from a pill (setRead, toggleFlag, upsertTask,
    // resolve) goes through patch() below, which registers it there. Keep it here anyway —
    // it is what covers any future open path that does not write a patch.
    opts?.onTouch?.(email.id);
    if (email.isRead) return;
    patch(email, { isRead: true });
    emailApi.markRead(email.mailbox, email.id, true).catch(() => {});
  };

  /** Replace the full Outlook category list (two-way sync). */
  const setCategories = async (email: UnifiedEmail, next: string[]) => {
    const prev = email.categories;
    patch(email, { categories: next });
    try {
      await emailApi.setEmailCategories(email.mailbox, email.id, next);
      return true;
    } catch (err: any) {
      patch(email, { categories: prev }); // revert
      toast({ title: "Could not update labels", description: err.message, variant: "destructive" });
      return false;
    }
  };

  /** One action over many rows, in ONE request. Per-item results come back from the server,
   * so only the rows that actually landed get patched — rolling the whole batch back because
   * 2 of 100 failed would be a lie in the other direction.
   *
   * ⚠️ Each successful row is patched through `patch()` above, deliberately and not by a
   * bespoke cache write: patch() fires `onTouch`, which is what keeps a bulk-marked-read row
   * listed under the Unread pill instead of vanishing mid-action (ENG-96 — the exact bug this
   * app already shipped once). Do not "optimize" this into a single cache write. */
  const bulkApply = async (
    emails: UnifiedEmail[],
    action: BulkEmailAction,
  ): Promise<{ succeeded: number; failed: number }> => {
    // Defense in depth — the surfaces never offer a checkbox on a personal row, and the
    // server rejects the write anyway (rejectIfPersonalMailboxTarget).
    const targets = emails.filter((e) => !isPersonalRow(e));
    if (targets.length === 0) return { succeeded: 0, failed: 0 };
    if (targets.length > BULK_MAX) {
      toast({
        title: `Select at most ${BULK_MAX} emails`,
        description: `${targets.length} selected — narrow the selection and try again.`,
        variant: "destructive",
      });
      // failed > 0 on purpose: callers clear the selection only when failed === 0, and the
      // toast just told the user to NARROW this selection — wiping it would leave nothing
      // to narrow (review finding, v1.5.62).
      return { succeeded: 0, failed: targets.length };
    }

    const params =
      action.type === "read"
        ? { is_read: action.isRead }
        : action.type === "assign"
          ? { assigned_to_user_id: action.user?.id ?? null }
          : action.type === "flag"
            ? { flagged: action.flagged }
            : { category: action.label };

    const ids = targets.map((e) => e.id);
    // A selection on /inbox can span clients — re-sync every client's account caches it
    // touched, not just this surface's (v1.5.71 seam fix).
    const touchedAccounts = new Set(
      targets.map((e) => accountIdForAction(scope, e)).filter((a): a is string => !!a),
    );
    const invalidateBulk = (extra: { waitingOn?: boolean } = {}) => {
      invalidateEmailSurfaces(queryClient, extra);
      touchedAccounts.forEach((accountId) => invalidateEmailSurfaces(queryClient, { accountId }));
    };
    setBulkBusy(true);
    setBusyForAll(ids, true);
    try {
      const res = await emailApi.bulkEmailAction({
        action: action.type,
        items: targets.map((e) => ({
          mailbox: e.mailbox,
          message_id: e.id,
          account_id: e.accountId || null,
        })),
        params,
      });

      const byId = new Map(targets.map((e) => [e.id, e]));
      for (const r of res.results) {
        if (!r.ok) continue;
        const email = byId.get(r.message_id);
        if (!email) continue;
        if (action.type === "read") {
          patch(email, { isRead: action.isRead });
        } else if (action.type === "assign") {
          patch(email, {
            taskStatus: action.user ? "assigned" : "new",
            assignedToUserId: action.user?.id ?? null,
            assignedToName: action.user?.name ?? null,
            // Assigning marks read server-side (claim→read, Session 195) — mirror upsertTask.
            ...(action.user ? { isRead: true } : {}),
          });
        } else if (action.type === "flag") {
          patch(email, { flagStatus: action.flagged ? "flagged" : "notFlagged" });
        } else {
          // Categories merge server-side; take its truth rather than guessing the union.
          patch(email, { categories: r.categories ?? [] });
        }
      }

      const n = res.succeeded;
      const noun = n === 1 ? "email" : "emails";
      const title =
        action.type === "read"
          ? `${n} ${noun} marked ${action.isRead ? "read" : "unread"}`
          : action.type === "assign"
            ? action.user
              ? `${n} ${noun} assigned to ${action.user.name}`
              : `${n} ${noun} unassigned`
            : action.type === "flag"
              ? `${n} ${noun} ${action.flagged ? "flagged" : "unflagged"}`
              : `Label "${action.label}" applied to ${n} ${noun}`;

      if (res.failed > 0) {
        const firstError = res.results.find((r) => !r.ok)?.error;
        toast({
          title: `${res.failed} of ${res.results.length} failed`,
          description: firstError ?? title,
          variant: "destructive",
        });
      } else {
        toast({ title });
      }

      invalidateBulk(action.type === "flag" ? { waitingOn: true } : {});
      return { succeeded: res.succeeded, failed: res.failed };
    } catch (err: any) {
      toast({ title: "Bulk action failed", description: err.message, variant: "destructive" });
      // Nothing was patched, so there is nothing to roll back — but re-sync anyway in case
      // the request landed server-side and only the response was lost.
      invalidateBulk();
      return { succeeded: 0, failed: targets.length };
    } finally {
      setBusyForAll(ids, false);
      setBulkBusy(false);
    }
  };

  const pinToProject = async (email: UnifiedEmail, project: { id: string; title: string }) => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("pinToProject"); return false; }
    setBusyFor(email.id, true);
    try {
      await emailApi.pinEmail({
        project_id: project.id,
        graph_message_id: email.id,
        mailbox_address: email.mailbox,
        subject: email.subject,
        sender_name: email.fromName,
        sender_email: email.fromAddress,
        snippet: email.snippet,
        received_at: email.receivedDateTime,
      });
      patch(email, { pinned: true, pinnedProjectId: project.id, pinnedProjectTitle: project.title });
      toast({ title: "Pinned to project", description: project.title });
      invalidateEmailSurfaces(queryClient, { ...acctOpt(email), projectId: project.id });
      return true;
    } catch (err: any) {
      toast({ title: "Could not pin", description: err.message, variant: "destructive" });
      return false;
    } finally {
      setBusyFor(email.id, false);
    }
  };

  /** Forward the whole invoice email to the client's MakersHub box (resolves the task). */
  const forwardToMakersHub = async (email: UnifiedEmail) => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("forwardToMakersHub"); return false; }
    setBusyFor(email.id, true);
    try {
      const result = await forwardInvoiceToMakersHub({
        accountId: email.accountId,
        mailbox: email.mailbox,
        message_id: email.id,
        subject: email.subject ?? undefined,
      });
      patch(email, { taskStatus: "resolved" });
      toast({ title: "Forwarded to MakersHub", description: `Sent to ${result.forwarded_to} — marked resolved.` });
      invalidateEmailSurfaces(queryClient, acctOpt(email));
      return true;
    } catch (err: any) {
      toast({ title: "Forward failed", description: err.message, variant: "destructive" });
      return false;
    } finally {
      setBusyFor(email.id, false);
    }
  };

  /** Submit one attachment's bytes to MakersHub (needs the account's API key). */
  const [mhSubmitting, setMhSubmitting] = useState<Set<string>>(new Set());
  const [mhSent, setMhSent] = useState<Set<string>>(new Set());
  const submitAttachmentToMakersHub = async (
    email: UnifiedEmail,
    att: { id: string; name: string; contentType: string },
  ) => {
    setMhSubmitting((prev) => new Set([...prev, att.id]));
    try {
      const { getSupabase } = await import("@/lib/supabase");
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const url = `/api/emails/message-attachments/download?mailbox=${encodeURIComponent(email.mailbox)}&messageId=${encodeURIComponent(email.id)}&attachmentId=${encodeURIComponent(att.id)}&filename=${encodeURIComponent(att.name)}`;
      const resp = await fetch(url, {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (!resp.ok) throw new Error("Failed to fetch attachment");
      const blob = await resp.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const result = await fetchWithAuth<{ invoiceId: number }>(
        `/api/accounts/${email.accountId}/makershub/submit-invoice`,
        { method: "POST", body: JSON.stringify({ data: base64, filename: att.name, type: att.contentType }) },
      );
      setMhSent((prev) => new Set([...prev, att.id]));
      toast({ title: "Sent to MakersHub", description: `Invoice #${result.invoiceId} created` });
      return true;
    } catch (err: any) {
      toast({ title: "MakersHub submission failed", description: err.message, variant: "destructive" });
      return false;
    } finally {
      setMhSubmitting((prev) => {
        const next = new Set(prev);
        next.delete(att.id);
        return next;
      });
    }
  };

  /** Save the email (subject/from/date/body) as a client note on its account. Fetches the
   * body if it isn't already loaded (row collapsed / cache miss) rather than saving a
   * silently empty note. */
  const saveAsNote = async (email: UnifiedEmail): Promise<boolean> => {
    if (isPersonalRow(email)) { warnPersonalRowNoOp("saveAsNote"); return false; }
    setBusyFor(email.id, true);
    try {
      let bodyText: string | null = email.snippet ?? null;
      const cached = queryClient.getQueryData<{ content: string; contentType: string }>([
        "email-body",
        email.mailbox,
        email.id,
      ]);
      if (cached?.content) {
        bodyText = cached.content;
      } else {
        try {
          const full = await emailApi.getEmailBody(email.mailbox, email.id);
          if (full.body?.content) bodyText = full.body.content;
        } catch {
          // Fall back to the snippet already captured above — still better than nothing.
        }
      }
      const { title, plain_text } = formatEmailAsNote({
        subject: email.subject,
        fromName: email.fromName,
        fromAddress: email.fromAddress,
        receivedDateTime: email.receivedDateTime,
        bodyText,
      });
      await api.createAccountNote(email.accountId, { title, plain_text });
      toast({ title: "Saved to client notes" });
      return true;
    } catch (err: any) {
      toast({ title: "Could not save note", description: err.message, variant: "destructive" });
      return false;
    } finally {
      setBusyFor(email.id, false);
    }
  };

  return {
    scope,
    busy,
    upsertTask,
    claim,
    setStatus,
    toggleFlag,
    setRead,
    markReadLocal,
    setCategories,
    bulkApply,
    bulkBusy,
    pinToProject,
    saveAsNote,
    forwardToMakersHub,
    submitAttachmentToMakersHub,
    mhSubmitting,
    mhSent,
  };
}

export type EmailActions = ReturnType<typeof useEmailActions>;
